import type { ReferenceOption } from "@fieldpath/shared";
import { useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { Fetcher } from "../api/client";
import { SearchSelect } from "./SearchSelect";

/**
 * REQUESTED WORK (punch list F140): the line a request shows while it waits
 * for a PM, and -- to anybody Deltek flags PM, or an admin
 * (`editability.decideRequest`, the server's own answer) -- Approve request
 * and Reject request.
 *
 * Approving names its PM / Task Manager: the project's PM unless the approver
 * changes it, which the server applies when nothing is sent. Rejecting is the
 * ordinary cancel, so it asks the cancel's question for its reason.
 *
 * Only the words Jeremy approved are new here: "Requested", the line, and
 * the two buttons. Everything else is the cancel box's and the PM line's.
 */
export function RequestPanel({
  fetcher,
  kind,
  id,
  projectId,
  mayDecide,
  onDecided,
}: {
  fetcher: Fetcher;
  kind: "tasks" | "deliverables";
  id: string;
  projectId: string;
  mayDecide: boolean;
  onDecided: () => Promise<void> | void;
}) {
  const [managers, setManagers] = useState<ReferenceOption[]>([]);
  const [pm, setPm] = useState<{ key: string; name: string } | null>(null);
  const [changing, setChanging] = useState(false);
  const [owner, setOwner] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mayDecide) return;
    void api
      .getProject(fetcher, projectId)
      .then((p) => setPm(p.pmKey ? { key: p.pmKey, name: p.pmName ?? p.pmKey } : null))
      .catch(() => undefined);
    void api.projectManagers(fetcher).then(setManagers).catch(() => undefined);
  }, [fetcher, projectId, mayDecide]);

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onDecided();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const chosenName =
    owner === null
      ? (pm?.name ?? "Unassigned")
      : owner === ""
        ? "Unassigned"
        : (managers.find((m) => m.key === owner)?.label ?? owner);

  return (
    <div className="request-banner" role="status">
      <span className="request-banner-tag">Requested</span>
      <div className="request-banner-body">
        <strong>Waiting for a PM to approve this request.</strong>
        {mayDecide ? (
          <>
            <div className="request-pm">
              <span className="muted">PM / Task Manager</span>{" "}
              {changing ? (
                <SearchSelect
                  ariaLabel="PM / Task Manager"
                  options={managers}
                  value={owner ?? pm?.key ?? ""}
                  emptyLabel="Unassigned"
                  onChange={(key) => {
                    setOwner(key);
                    setChanging(false);
                  }}
                />
              ) : (
                <>
                  <span>{chosenName}</span>{" "}
                  <button className="linkish" onClick={() => setChanging(true)} disabled={busy}>
                    Change
                  </button>
                </>
              )}
            </div>
            {rejecting ? (
              <div className="cancel-task-form">
                <label htmlFor="reject-reason">Why is this being cancelled?</label>
                <textarea
                  id="reject-reason"
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Client withdrew the scope, duplicate of another task, …"
                  autoFocus
                />
                <div className="row">
                  <button
                    className="rework"
                    disabled={busy || reason.trim().length === 0}
                    onClick={() => void run(() => api.rejectRequest(fetcher, kind, id, reason))}
                  >
                    Reject request
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => {
                      setRejecting(false);
                      setReason("");
                    }}
                  >
                    Keep it
                  </button>
                </div>
              </div>
            ) : (
              <div className="row request-actions">
                <button
                  className="primary"
                  disabled={busy}
                  onClick={() =>
                    void run(() => api.approveRequest(fetcher, kind, id, owner === null ? undefined : owner || null))
                  }
                >
                  Approve request
                </button>
                <button disabled={busy} onClick={() => setRejecting(true)}>
                  Reject request
                </button>
              </div>
            )}
            {error ? <span className="error-inline">{error}</span> : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

/** The tag beside a status: "Requested", while a request waits. */
export function RequestedChip() {
  return (
    <span className="chip requested" style={{ marginLeft: 6 }}>
      Requested
    </span>
  );
}
